import { deiterateAPI } from "../utils/deiterateAPI.util";
import { window } from "../utils/window.util";

export namespace AssuranceLibrary {

    export interface ChangeItem {
        elementName: string;
        newValue: any;
        oldValue: any;
    }

    export interface TaskHistoryItem {
        changedBy: string;
        changedDate: Date;
        changes: {
            [key: string]: ChangeItem;
        };
    }

    export interface TaskEntity {
        componentType: string;
        taskCat: string;
        taskCriticality: string;
        taskDesc: string;
        taskHistory: TaskHistoryItem[];
        taskID: string;
        taskIDstatus: "active" | "inactive" | "deleted";
        taskLocation: string;
        taskOwner: string;
        taskPublishInPrivacyPolicy: "Yes" | "No";
        taskRisk: string;
        taskSubprocessor: "Yes" | "No";
        taskTeam: string;
        backupLocation: string;
        backupNeeded: "Yes" | "No";
        backupRetention: string;
        classification: string;
        customFields: any[];
        dataStorageLocations: string[];
        dataTypesShared: string[];
        dataTypesSharedString: string;
        dateAdded: Date;
        label: string;
        lastReviewDate: Date;
        legalBits: string;
        nextReviewDate: Date;
        portability: "Yes" | "No";
        securityReview: "Yes" | "No";
        serial: string;
        socCriticality: string;
        socSubserviceOrganisation: string;
        status: "Active" | "Decommissioned" | "Deleted";
        vendorManagementNeeded: "Yes" | "No";
        vendorWebsite: string;
    }

    export interface AssuranceLibraryResponse {
        statusCode?: number;
        message?: string;
        tasks?: TaskEntity[];
        entry?: TaskEntity;
        error?: string | null;
    }

    export class TaskInstance {
        private data: AssuranceLibrary.TaskEntity;

        constructor(data: AssuranceLibrary.TaskEntity) {
            this.data = data;
        }

        // A getter to access the underlying data if needed
        public get entity(): AssuranceLibrary.TaskEntity {
            return this.data;
        }

        // An edit method that calls the static Edit function on the Task class
        public async edit(updates: Partial<AssuranceLibrary.TaskEntity>): Promise<AssuranceLibrary.AssuranceLibraryResponse | void> {
            return AssuranceLibrary.AssuranceTask.Edit(this.data.taskID, updates);
        }
    }

    export class AssuranceTask {

        /**
         * Add an task to the API.
         * @param id - The ID of the task to add.
         * @param taskData - Partial task data to add.
         * @param callback - Optional callback after response.
         */
        static async Add(
            id: string,
            taskData: Partial<TaskEntity>,
            callback?: (res: AssuranceLibraryResponse) => any
        ): Promise<AssuranceLibraryResponse | void> {
            console.log("Calling API Add", { companyID: window.companyID, ...taskData });

            const res = await deiterateAPI.POST(`/deiterateInternalAPI/assurance/add/${id}`, {
                mode: 'cors',
                body: JSON.stringify({ companyID: window.companyID, ...taskData })
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
            }
        }

        /**
        * Perform a task via the API.
        * @param id - The ID of the task to perform.
        * @param taskData - Partial task data to perform.
        * @param callback - Optional callback after response.
        */
        static async Perform(
            id: string,
            taskData: Partial<TaskEntity>,
            callback?: (res: AssuranceLibraryResponse) => any
        ): Promise<AssuranceLibraryResponse | void> {
            console.log("Calling API Perform", { companyID: window.companyID, ...taskData });

            const res = await deiterateAPI.POST(`/deiterateInternalAPI/assurance/perform/${id}`, {
                mode: 'cors',
                body: JSON.stringify({ companyID: window.companyID, ...taskData })
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
            }
        }



        /**
         * Delete an task from the API.
         * @param id - The ID of the task to delete.
         * @param callback - Optional callback after response.
         */
        static async Delete(
            id: string,
            callback?: (res: AssuranceLibraryResponse) => any
        ): Promise<AssuranceLibraryResponse | void> {
            console.log("Calling API Delete", { companyID: window.companyID });

            const res = await deiterateAPI.POST(`/deiterateInternalAPI/assurance/delete/${id}`, {
                mode: 'cors',
                body: JSON.stringify({ companyID: window.companyID })
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
            }
        }


        /**
         * Edit an existing task in the API.
         * @param id - The ID of the task to edit.
         * @param taskData - Partial task data to update.
         * @param callback - Optional callback after response.
         */
        static async Edit(
            id: string,
            taskData: Partial<TaskEntity>,
            callback?: (res: AssuranceLibraryResponse) => any
        ): Promise<AssuranceLibraryResponse | void> {
            console.log("Calling API Edit", { companyID: window.companyID, ...taskData });

            const res = await deiterateAPI.POST(`/deiterateInternalAPI/assurance/edit/${id}`, {
                mode: 'cors',
                body: JSON.stringify({ companyID: window.companyID, ...taskData })
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
            }
        }

        /**
         * Fetch all tasks from the API.
         */
        static async Get(): Promise<AssuranceLibrary.TaskEntity[] | undefined>;

        /**
         * Fetch a specific task by ID from the API.
         * @param id - The ID of the task to fetch.
         */
        static async Get(id: string | number): Promise<TaskInstance | undefined>;

        /**
         * Fetch a only a specific tasks from the API.
         * @param leaveNull | NULL
         * @param type - Standard of tasks to fetch (ISO27001, Essential 8, etc. - can be fetched by providing the type in the request parameter)
         */
        static async Get(leaveNull: null, type: string): Promise<TaskInstance | undefined>;

        static async Get(id?: string | number | null, type?: string): Promise<AssuranceLibrary.TaskEntity[] | TaskInstance | undefined> {
            if (id === null && type) {
                // Fetch all tasks
                const res = await deiterateAPI.POST(`/deiterateInternalAPI/assurance/get/standard|${type}`, {
                    mode: 'cors',
                    body: JSON.stringify({ companyID: window.companyID })
                });

                // Filter res based on type
                console.log('Filtered tasks by type: ', type);

                // If the type is not provided, return all tasks
                return res.tasks;
            } else if (typeof id === 'undefined' || id === 'all') {
                // Fetch all tasks
                const res = await deiterateAPI.POST(`/deiterateInternalAPI/assurance/get/all`, {
                    mode: 'cors',
                    body: JSON.stringify({ companyID: window.companyID })
                });
                return res.tasks;
            } else {
                // Fetch single task
                const res = await deiterateAPI.POST(`/deiterateInternalAPI/assurance/get/${id}`, {
                    mode: 'cors',
                    body: JSON.stringify({ companyID: window.companyID })
                });

                if (res.entry) {
                    return new TaskInstance(res.entry);
                } else {
                    return undefined;
                }
            }
        }
    }

    export const Task = new Proxy(AssuranceTask, {
        get(target, prop) {
            const lowerCasedProp = prop.toString().toLowerCase();
            if (lowerCasedProp === 'get') {
                return target.Get;
            } else if (lowerCasedProp === 'add') {
                return target.Add;
            } else if (lowerCasedProp === 'delete') {
                return target.Delete;
            } else if (lowerCasedProp === 'edit') {
                return target.Edit;
            }

            return target[lowerCasedProp];
        }
    });
}